/**
 * THE design-system atoms — one system, used by every screen in the product.
 *
 * History, because it explains the shape of this file. The club app was built
 * in a "race-bib" skin (Barlow Condensed caps, IBM Plex Mono numbers, rounded
 * cards on cold slate). The public home page was rebuilt on 26 Aug 2026 in a
 * different language, and the platform consoles were moved onto it the same
 * day via a twin module, `consoleUi.jsx`. The owner then asked for the club
 * admin and member screens too, so the twin was FOLDED BACK IN: these atoms
 * are now the marketing language, and `consoleUi.jsx` re-exports this file and
 * adds the few console-only pieces. There is no second vocabulary to drift.
 *
 * The rules:
 *
 *   - ONE typeface, Archivo (`font-market`). Numbers get `tabular-nums`, which
 *     is what the monospace was really being used for.
 *   - ZERO radius. Every corner in this design is square. The exceptions are
 *     genuinely round things — an avatar, a spinner, a count badge.
 *   - Warm ground (`bg-ground`, #FBFAF7), white cards, `line` hairlines.
 *   - THE GAP IS THE RULE. A row of cards is one bordered block with `gap-px`
 *     over a `bg-line` backing, so the dividers are the background showing
 *     through — not N floating boxes each with its own border and shadow.
 *   - Two accents: gold (#F6C019) is the primary voice, muted teal (`sea-600`)
 *     the secondary. Anything sitting ON gold takes ink text, never white.
 *   - Crimson (`flag`) is the alarm voice: counts, overdue, destructive.
 *
 * What did NOT change: every component's API (screens still say
 * `<Button tone="p">`, `<Pill tone="ok">`, `<Stat numberTone="flag">`), and
 * every status semantic — emerald/teal = ok, red = overdue or destructive,
 * gold = attention, periwinkle = pending. Only the pigments moved.
 *
 * The distance ramp (`d5`…`d42`) is untouched and still drives the bib's top
 * rule: it encodes DATA, not decoration.
 */

import { Children, cloneElement, isValidElement } from 'react';
import { useBackToClose } from '../hooks/useBackToClose';

/* ---------------- icons ---------------- */

/**
 * The glyph set. One flat stroke weight, sized by the caller.
 *
 * This lived inside AppShell.jsx while the nav was the only thing that needed
 * icons. It is an atom, so it belongs here — the profile-photo menu needs the
 * same four-stroke look as the sidebar, and a second copy would have drifted
 * from it within a week.
 */
const ICON_PATHS = {
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  people:
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  rupee: 'M6 4h12M6 9h12M16 4c0 4-3 5-6 5l7 10',
  calendar: 'M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zM4 10h16M9 3v4M15 3v4',
  stopwatch: 'M12 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16M12 10v4l2.5 2M9 2h6',
  heart: 'M12 20s-7-4.4-7-9.3A3.9 3.9 0 0 1 12 8a3.9 3.9 0 0 1 7 2.7c0 4.9-7 9.3-7 9.3z',
  plus: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 8v8M8 12h8',
  mail: 'M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 7l9 6 9-6',
  bib: 'M5 5h14v14H5zM9 3v3M15 3v3M8 12h8M8 16h5',
  // the race marshal's flag — an event this member has been put in charge of
  flag: 'M5 21V3M5 4h13l-2.5 4L18 12H5',
  medal: 'M12 15a5 5 0 1 0 0 10 5 5 0 0 0 0-10M8.2 13.5 5 3h5l2.5 7M15.8 13.5 19 3h-5',
  bell: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6L6 18',
  signout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  pencil: 'M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z',
  eye: 'M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
  folder: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  trash: 'M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.56-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34h.01A1.7 1.7 0 0 0 10 4.1V4a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.01c.26.63.87 1.04 1.55 1.04H21a2 2 0 1 1 0 4h-.09c-.68 0-1.29.41-1.51 1.04z',
};

/**
 * THE PLATFORM MARK — a yellow square, two forward-leaning stride slashes and
 * a track bar beneath them. From the marketing design handoff (26 Aug 2026).
 *
 * It lives here rather than in a page because the home page, every entry
 * screen and both shells draw it, and a logo that exists twice drifts.
 */
export function RunnerCrewMark({ size = 34, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true" className={className}>
      <rect width="40" height="40" fill="#F6C019" />
      <path d="M15 9h8l-6.2 17H8.8z" fill="#0B0F18" />
      <path d="M26 9h6l-6.2 17h-6z" fill="#0B0F18" opacity=".55" />
      <rect x="8.5" y="28.6" width="23" height="2.6" fill="#0B0F18" />
    </svg>
  );
}

export function Icon({ name, size = 16, strokeWidth = 1.9, className }) {
  const d = ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d={d} />
    </svg>
  );
}

/* ---------------- headings ---------------- */

/**
 * The section heading: a small teal eyebrow in wide caps, with an optional
 * figure hard right. Quieter than the old black display caps on purpose — on
 * a data screen the DATA should be the loudest thing, and the heading only has
 * to say which block you are looking at.
 */
export function Eyebrow({ children, right }) {
  return (
    /* No `first:mt-0` — headings are each wrapped in their own <Reveal>, so
       they are always the first child and the modifier would always fire. */
    <div className="mb-3.5 mt-9 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <h3 className="font-market text-[11px] font-bold uppercase tracking-[0.28em] text-sea-600">
        {children}
      </h3>
      {right && (
        <span className="font-market text-[11.5px] font-semibold tabular-nums text-copy-label">
          {right}
        </span>
      )}
    </div>
  );
}

/** Page title and subtitle — one per screen, above everything else. */
export function ViewTitle({ title, sub }) {
  return (
    <>
      <h2 className="font-market text-[28px] font-extrabold leading-[1.08] tracking-[-0.032em] text-ink-700 md:text-[34px]">
        {title}
      </h2>
      {sub !== undefined && (
        <div className="mb-5 mt-2.5 font-market text-[14.5px] leading-[1.6] text-copy-muted">
          {sub}
        </div>
      )}
    </>
  );
}

/* ---------------- status pill ---------------- */

// Pill tones: 'late' | 'soon' | 'ok' | 'pend' | 'sent' | 'grey'
//
// Status semantics survive every reskin. `pend` deliberately does NOT use the
// accent: the accent is gold, and a gold "Never paid" would be
// indistinguishable from a gold "4 days left". Periwinkle keeps the two apart
// and is used for nothing else.
const PILL_TONE = {
  late: 'bg-flag-50 text-flag-600',
  soon: 'bg-gold-50 text-gold-800',
  ok: 'bg-sea-50 text-sea-600',
  pend: 'bg-indigo-50 text-indigo-600',
  sent: 'bg-line-chip text-copy',
  grey: 'bg-line-chip text-copy',
};

/**
 * `self-center` matters: a pill is often a flex child in a row next to a
 * Button, and the default `align-items: stretch` would blow it up to the
 * button's height — a tall lozenge with the text pinned to the top.
 */
export function Pill({ tone = 'grey', children }) {
  return (
    <span
      className={`inline-flex items-center self-center whitespace-nowrap px-2 py-[3px] font-market text-[10px] font-bold uppercase leading-normal tracking-[0.08em] ${
        PILL_TONE[tone] ?? PILL_TONE.grey
      }`}
    >
      {children}
    </span>
  );
}

/* ---------------- buttons ---------------- */

// Button tones: 'p' | 'g' | 'v' | 'd' | 'r'
//   p = the gold CTA — primary actions. Ink text, NOT white (contrast).
//   v = legacy volunteer tone, now an alias of p
//   d = solid ink with a gold label — secondary-strong actions
//   r = crimson — destructive/emergency actions, AND the CTA inside a crimson
//       alert banner, where a gold button would fight the banner it sits in
//   g = ghost — cancel and tertiary
const BTN_TONE = {
  p: 'bg-[linear-gradient(140deg,#FFD75E,#F6C019_60%,#EFB000)] text-ink-800 shadow-[0_12px_26px_-16px_rgba(246,192,25,.95)] hover:-translate-y-px hover:brightness-105',
  v: 'bg-[linear-gradient(140deg,#FFD75E,#F6C019_60%,#EFB000)] text-ink-800 shadow-[0_12px_26px_-16px_rgba(246,192,25,.95)] hover:-translate-y-px hover:brightness-105',
  d: 'bg-ink-800 text-gold-500 hover:-translate-y-px hover:bg-ink-700',
  r: 'bg-flag-500 text-white hover:bg-flag-600',
  g: 'border border-line-strong bg-white text-ink-700 hover:border-ink-700 hover:bg-ground-warm',
};

export function Button({ tone = 'g', small, block, className, ...rest }) {
  const classes = [
    'inline-flex items-center justify-center gap-1.5 font-market font-extrabold tracking-[-0.005em] transition-all duration-200 active:translate-y-0 disabled:pointer-events-none disabled:opacity-60',
    small ? 'px-3.5 py-2 text-[12.5px]' : 'px-5 py-2.5 text-[13.5px]',
    block ? 'w-full' : '',
    BTN_TONE[tone] ?? BTN_TONE.g,
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return <button type="button" className={classes} {...rest} />;
}

/** A row of buttons that wraps on a narrow phone. */
export function ButtonRow({ children, style }) {
  return (
    <div className="flex flex-wrap gap-2.5" style={style}>
      {children}
    </div>
  );
}

/* ---------------- surfaces ---------------- */

/** The plain white panel: a hairline, no radius, no shadow. */
export function Card({ children, className = '' }) {
  return <div className={`border border-line bg-white ${className}`}>{children}</div>;
}

/**
 * A row or grid of cells divided by the background rather than by borders.
 * Children should be plain `bg-white` blocks with no border of their own.
 */
export function CardGrid({ children, className = '' }) {
  return <div className={`grid gap-px border border-line bg-line ${className}`}>{children}</div>;
}

/* ---------------- stats ---------------- */

// numberTone: 'good' | 'bad' | 'flag' | 'amber' — colours the NUMBER. The cell
// itself is always plain white: in a strip of counters a coloured figure reads
// faster than a coloured card, and four tinted cards turn into noise.
//
// `tone` is still accepted so no caller had to change, but it draws nothing.
const STAT_NUMBER = {
  good: 'text-sea-600',
  bad: 'text-flag-500',
  flag: 'text-flag-500',
  amber: 'text-gold-700',
};

export function Stat({ value, label, numberTone, onClick }) {
  const body = (
    <>
      {/*
        `[&_small]:tracking-normal [&_span]:tracking-normal` is not cosmetic.
        Callers pass a fragment for `value` — `{km}<span> km</span>` — and a
        nested label INHERITS this element's -0.03em. On a label small enough
        to annotate the figure that is most of a word space, so " km" renders
        glued to the number. Resetting it here fixes every caller at once
        rather than waiting for each one to notice.
      */}
      <div
        className={`font-market text-[32px] font-extrabold leading-none tracking-[-0.03em] tabular-nums [&_small]:tracking-normal [&_span]:tracking-normal ${
          STAT_NUMBER[numberTone] ?? 'text-ink-700'
        }`}
      >
        {value}
      </div>
      <div className="mt-2.5 flex items-baseline justify-between gap-2 font-market text-[10.5px] font-bold uppercase tracking-[0.16em] text-copy-label">
        {label}
        {/* the affordance: a clickable counter openly says where it goes */}
        {onClick && (
          <span aria-hidden="true" className="text-sea-600">
            →
          </span>
        )}
      </div>
    </>
  );

  // No border and no radius: the cell sits inside <Stats>, whose `gap-px` over
  // a `bg-line` backing draws every divider. A border here would double them.
  const frame = 'bg-white px-5 py-[18px]';

  // A counter that is also a door: clicking it opens the list it counts.
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${frame} block w-full cursor-pointer text-left transition-colors duration-300 hover:bg-ground-warm`}
      >
        {body}
      </button>
    );
  }

  return <div className={frame}>{body}</div>;
}

/**
 * The stat strip: 2-across on a phone, N-across from 680px, hairline-divided.
 *
 * The column count follows the number of children, and an odd last child is
 * stretched across both phone columns. That is not tidiness — it is required.
 * The dividers here are the `bg-line` backing showing through `gap-px`, so an
 * unfilled cell is not empty space the way it was when these were separate
 * floating cards: it renders as a solid beige block.
 */
const STATS_COLS = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 min-[680px]:grid-cols-3 [&>*:last-child]:col-span-2 min-[680px]:[&>*:last-child]:col-span-1',
  4: 'grid-cols-2 min-[680px]:grid-cols-4',
  5: 'grid-cols-2 min-[680px]:grid-cols-5 [&>*:last-child]:col-span-2 min-[680px]:[&>*:last-child]:col-span-1',
  6: 'grid-cols-2 min-[680px]:grid-cols-6',
};

export function Stats({ children, style }) {
  const count = Children.toArray(children).length;
  return (
    <div
      className={`grid gap-px border border-line bg-line ${STATS_COLS[count] ?? STATS_COLS[4]}`}
      style={style}
    >
      {children}
    </div>
  );
}

/* ---------------- empty states ---------------- */

export function Empty({ title, children, padding }) {
  return (
    <div
      className="border border-line bg-white px-5 py-14 text-center"
      style={padding ? { padding } : undefined}
    >
      {title && (
        <div className="font-market text-[15px] font-extrabold uppercase tracking-[0.14em] text-copy-label">
          {title}
        </div>
      )}
      {children && (
        <p className="mx-auto mt-2 max-w-sm font-market text-[13.5px] leading-[1.6] text-copy-muted">
          {children}
        </p>
      )}
    </div>
  );
}

/* ---------------- tables ---------------- */

/** The plain text inside a React node — what a <th> says, for a data-label. */
function nodeText(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (isValidElement(node)) return nodeText(node.props.children);
  return '';
}

/** Column names, in order, from the <tr> of <th>s a caller passes as `head`. */
function headLabels(head) {
  const tr = Children.toArray(head).find(isValidElement);
  if (!tr) return [];
  return Children.toArray(tr.props.children).map((th) =>
    isValidElement(th) ? nodeText(th.props.children).trim() : '',
  );
}

/**
 * Stamp each <td> with the name of its column, so the phone layout
 * (`.table-stack`, tailwind.config.js) can print it beside the value. A cell
 * under an empty header — the actions column — gets no label and is simply
 * right-aligned. Rows that are not literal <tr> elements pass through as is.
 */
function labelCells(rows, labels) {
  return Children.map(rows, (row) => {
    if (!isValidElement(row) || row.type !== 'tr') return row;
    let col = 0;
    const cells = Children.map(row.props.children, (cell) => {
      if (!isValidElement(cell) || cell.type !== 'td') return cell;
      const label = labels[col] ?? '';
      col += Number(cell.props.colSpan) || 1;
      if (!label || cell.props['data-label'] !== undefined) return cell;
      return cloneElement(cell, { 'data-label': label });
    });
    return cloneElement(row, undefined, cells);
  });
}

/**
 * A hairline-framed table. From `sm` up it is a real table that scrolls
 * sideways if it must; below that every row stacks into a labelled card,
 * because a phone cannot show a sideways scroll and the columns that got cut
 * were the buttons (see `.table-stack` in tailwind.config.js).
 */
export function Table({ head, children }) {
  const labels = headLabels(head);
  return (
    <div className="border border-line bg-white">
      <div className="overflow-x-auto">
        <table className="table-stack w-full border-collapse font-market text-[13.5px] text-copy sm:min-w-[480px]">
          <thead className="[&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-line [&_th]:bg-ground [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:font-market [&_th]:text-[10px] [&_th]:font-bold [&_th]:uppercase [&_th]:tracking-[0.16em] [&_th]:text-copy-label">
            {head}
          </thead>
          <tbody className="sm:[&_td]:border-t sm:[&_td]:border-line sm:[&_td]:px-4 sm:[&_td]:py-3 [&_tr]:transition-colors [&_tr:hover]:bg-ground-warm">
            {labelCells(children, labels)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The in-table link — teal, so a row's one clickable thing is obvious. */
export function TableLink({ children, className = '' }) {
  return (
    <span
      className={`font-market font-bold text-sea-600 underline decoration-sea-600/30 underline-offset-[3px] transition-colors hover:decoration-sea-600 ${className}`}
    >
      {children}
    </span>
  );
}

/* ---------------- form fields ---------------- */

/** Shared input recipe — spread onto raw <input>/<select>/<textarea>. */
export const INPUT_CLS =
  'w-full border border-line-strong bg-white px-3.5 py-2.5 font-market text-[14px] text-ink-700 outline-none transition-all duration-200 placeholder:text-copy-label/70 focus:border-gold-500 focus:ring-4 focus:ring-gold-500/20';

/** Multi-line variant — same frame, room to type, resizes vertically only. */
export const TEXTAREA_CLS = `${INPUT_CLS} min-h-[90px] resize-y leading-relaxed`;

/**
 * `required` adds the crimson asterisk. The label styles cascade to the direct
 * input child via the recipe above — every sheet uses this.
 */
export function Field({ label, required, children }) {
  return (
    <div className="mb-4 [&_input]:w-full [&_input]:border [&_input]:border-line-strong [&_input]:bg-white [&_input]:px-3.5 [&_input]:py-2.5 [&_input]:font-market [&_input]:text-[14px] [&_input]:outline-none [&_input]:transition-all [&_input]:focus:border-gold-500 [&_input]:focus:ring-4 [&_input]:focus:ring-gold-500/20 [&_select]:w-full [&_select]:border [&_select]:border-line-strong [&_select]:bg-white [&_select]:px-3.5 [&_select]:py-2.5 [&_select]:font-market [&_select]:text-[14px] [&_select]:outline-none [&_select]:focus:border-gold-500 [&_select]:focus:ring-4 [&_select]:focus:ring-gold-500/20">
      <label
        className={`mb-2 block font-market text-[10px] font-bold uppercase tracking-[0.16em] text-copy-label ${
          required ? "after:text-flag-500 after:content-['_*']" : ''
        }`}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * One choice from a few, as side-by-side option boxes — a radio group that
 * shows each option's consequence under its label. `options`: [{ value,
 * label, note? }]. Keyboard: Tab into the group, arrows/Space via the radios.
 */
export function Choice({ name, value, onChange, options }) {
  return (
    <div role="radiogroup" className="grid grid-cols-2 gap-3">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <label
            key={o.value}
            className={`flex cursor-pointer items-start gap-2.5 border px-3.5 py-3 transition-colors focus-within:ring-4 focus-within:ring-gold-500/20 ${
              on ? 'border-gold-500 bg-gold-50' : 'border-line-strong bg-white hover:border-ink-700/40'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={on}
              onChange={() => onChange(o.value)}
              className="mt-[3px] !w-auto accent-[#1A2230]"
            />
            <span className="min-w-0">
              <span className="block font-market text-[14px] font-bold text-ink-700">{o.label}</span>
              {o.note && (
                <span className="mt-0.5 block font-market text-[12px] leading-snug text-copy-muted">
                  {o.note}
                </span>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/** Two fields side by side. */
export function Row2({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

/**
 * An Indian mobile number input: a fixed +91 prefix and the ten digits after
 * it, nothing else. The club is in Tirupur — every member is on an Indian
 * mobile — so a country selector is a control nobody needs and one more way
 * to store the same number three ways (owner's direction, 17 Aug 2026).
 *
 * The VALUE contract stays what the forms already speak: `onChange` receives
 * the full `+91XXXXXXXXXX` string ('' when emptied), so normalizePhone, the
 * duplicate checks and the API see exactly what they always saw. Pastes of
 * `+91…`, `91…` and `0…` shapes are unwrapped to the national digits.
 */
export function PhoneInput({ value, onChange, name, placeholder = '98765 43210' }) {
  const digits = nationalDigits(value);

  function handle(raw) {
    let d = String(raw).replace(/\D/g, '');
    // Unwrap a pasted international or trunk form down to the ten digits.
    if (d.length > 10 && d.startsWith('91')) d = d.slice(2);
    d = d.replace(/^0+/, '').slice(0, 10);
    onChange(d ? `+91${d}` : '');
  }

  return (
    <div className="flex w-full items-stretch overflow-hidden border border-line-strong bg-white transition-all duration-200 focus-within:border-gold-500 focus-within:ring-4 focus-within:ring-gold-500/20">
      <span className="flex select-none items-center border-r border-line-strong bg-ground px-3 font-market text-[13px] tabular-nums text-copy-label">
        +91
      </span>
      {/* The `!` overrides beat the Field cascade — inside <Field> every input
          gets its own border and focus ring, which would double the frame. */}
      <input
        type="tel"
        inputMode="numeric"
        name={name}
        autoComplete="off"
        maxLength={10}
        value={digits}
        placeholder={placeholder}
        onChange={(e) => handle(e.target.value)}
        className="min-w-0 flex-1 !rounded-none !border-0 bg-transparent px-3 py-2.5 font-market text-[14px] tabular-nums outline-none !ring-0 focus:!border-0 focus:!ring-0"
      />
    </div>
  );
}

/**
 * A phone number of ANY shape — mobile, landline, short local, international.
 * For the emergency contact only (owner's direction, 2 Oct 2026): the person
 * to call is not always on an Indian mobile, so the fixed +91 box above was
 * the wrong control. Free text; data/emergency.js decides whether it can be
 * dialled and how it is stored. Inherits the input frame from <Field>.
 */
export function AnyPhoneInput({ value, onChange, name, placeholder = 'Any number (optional)' }) {
  return (
    <input
      type="tel"
      inputMode="tel"
      name={name}
      autoComplete="off"
      maxLength={24}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="tabular-nums"
    />
  );
}

/** The ten national digits behind whatever shape a +91 number is stored in. */
function nationalDigits(value) {
  let v = String(value ?? '');
  // Our own stored shape FIRST, by prefix — a short value mid-typing ('+919')
  // must round-trip to '9', not have the country code bleed into the digits.
  if (v.startsWith('+91')) v = v.slice(3);
  let d = v.replace(/\D/g, '');
  // Legacy/pasted shapes that still carry the country code as digits.
  if (d.length > 10 && d.startsWith('91')) d = d.slice(2);
  return d.replace(/^0+/, '').slice(0, 10);
}

/** One read-only label/value pair. Missing values read "Not set". */
export function Fact({ k, v, mono }) {
  return (
    <div>
      <div className="font-market text-[10px] font-bold uppercase tracking-[0.16em] text-copy-label">
        {k}
      </div>
      {/* `overflow-wrap:anywhere`: an email address has no break point, and
          in a two-column fact grid on a phone it ran past the card edge. */}
      <div
        className={`mt-1.5 font-market text-[14px] font-semibold text-ink-700 [overflow-wrap:anywhere] ${
          mono ? 'tabular-nums' : ''
        } ${v ? '' : 'font-normal italic text-copy-label'}`}
      >
        {v || 'Not set'}
      </div>
    </div>
  );
}

/** Explanatory text under a field. */
export function Hint({ children, style, tone }) {
  // `bad` borrows FormError's crimson rather than inventing a third error
  // colour — a hint that has become a problem should look like every other
  // problem.
  const toneCls = tone === 'bad' ? 'text-flag-600' : 'text-copy-label';
  return (
    <div
      className={`mt-2 font-market text-[12px] leading-[1.6] ${toneCls} [&_b]:text-copy [&_strong]:text-copy`}
      style={style}
    >
      {children}
    </div>
  );
}

/** A validation message. Empty renders nothing, so layout does not jump. */
export function FormError({ children }) {
  if (!children) return null;
  return (
    <div className="mt-2 font-market text-[12.5px] font-semibold text-flag-600">{children}</div>
  );
}

/**
 * A busy indicator, sized to whatever it sits in.
 *
 * A gold ring on a faint track — the one accent, used here because a spinner
 * is the app telling you it is working, and that is the same voice as every
 * other primary action. `size` is a Tailwind dimension pair.
 */
export function Spinner({ size = 'h-6 w-6', label = 'Working' }) {
  return (
    <span
      role="status"
      aria-label={label}
      className={`${size} inline-block animate-spin rounded-full border-2 border-line-strong border-t-gold-500`}
    />
  );
}

/* ---------------- sheets & modals ---------------- */

/** The cancel/confirm pair at the foot of a sheet. */
export function SheetActions({ children }) {
  return <div className="mt-6 flex gap-2.5 [&>button]:flex-1">{children}</div>;
}

/** Sheet heading and subtitle. */
export function SheetHead({ title, sub }) {
  return (
    <>
      <h3 className="font-market text-[21px] font-extrabold leading-[1.1] tracking-[-0.03em] text-ink-700">
        {title}
      </h3>
      {sub !== undefined && (
        <div className="mb-5 mt-2 font-market text-[13.5px] leading-[1.55] text-copy-muted">
          {sub}
        </div>
      )}
    </>
  );
}

/**
 * The dialog frame. Every console and club modal was repeating the same three
 * divs — an ink scrim, a white box, a title — with slightly different paddings
 * each time; this is that, once.
 */
export function Modal({ title, sub, onClose, children, wide }) {
  // Back closes it — but only a modal that can be closed at all.
  useBackToClose(Boolean(onClose), onClose);
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink-900/70 p-4 backdrop-blur-[2px] motion-safe:animate-fadein"
      role="dialog"
      onMouseDown={onClose ? (e) => e.target === e.currentTarget && onClose() : undefined}
    >
      <div
        className={`w-full border border-line bg-white shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)] ${
          wide ? 'max-h-[85vh] max-w-xl overflow-auto' : 'max-w-sm'
        }`}
      >
        {title && (
          <div className="border-b border-line px-5 py-4">
            <p className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">
              {title}
            </p>
            {sub && (
              <p className="mt-1 font-market text-[13px] leading-[1.5] text-copy-muted">{sub}</p>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

/* ---------------- navigation ---------------- */

/** Step back up a level. */
export function BackButton({ children, onClick }) {
  return (
    <button
      type="button"
      className="group mb-4 inline-flex items-center gap-1.5 border border-line-strong bg-white px-3.5 py-2 font-market text-[11px] font-bold uppercase tracking-[0.14em] text-copy-muted transition-all duration-200 hover:border-ink-700 hover:text-ink-700"
      onClick={onClick}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="transition-transform duration-200 group-hover:-translate-x-0.5"
      >
        <path d="M15 18l-6-6 6-6" />
      </svg>
      {children}
    </button>
  );
}

/* ---------------- motion ---------------- */

/**
 * A block that settles in on first paint; `index` places it in the stagger.
 * Load only — never scroll-triggered. `motion-safe` honours reduced motion.
 *
 * `enabled` exists because a stagger is a WELCOME, not a page style. Replaying
 * it every time you land on a screen you have already seen turns a flourish
 * into a wait (owner's direction, 26 Aug 2026, about the club chooser). Pass
 * `enabled={false}` on a revisit and the same markup renders instantly.
 */
export function Reveal({ index = 0, children, className, enabled = true }) {
  if (!enabled) return <div className={className}>{children}</div>;
  return (
    <div
      className={`motion-safe:animate-rise${className ? ` ${className}` : ''}`}
      style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
    >
      {children}
    </div>
  );
}

/* ---------------- misc ---------------- */

/** A slim accent rule — the gold-to-teal hairline the whole product uses. */
export function Lane({ className, style }) {
  return (
    <div
      className={`h-[2px] bg-[linear-gradient(90deg,#F6C019,#7FD8C8)]${
        className ? ` ${className}` : ''
      }`}
      style={style}
    />
  );
}

/** A year divider in a history list. */
export function YearSplit({ children }) {
  return (
    <div className="mb-3 mt-6 flex items-center gap-3 font-market text-[10px] font-bold uppercase tracking-[0.2em] text-copy-label after:h-px after:flex-1 after:bg-line after:content-['']">
      {children}
    </div>
  );
}

/** The three-across summary strip above a history list. */
export function SummaryRow({ children, style }) {
  return (
    <div className="mb-1 grid grid-cols-3 gap-px border border-line bg-line" style={style}>
      {children}
    </div>
  );
}

export function SummaryCell({ value, label, color }) {
  return (
    <div className="bg-white px-3 py-4 text-center">
      <div
        className="font-market text-[22px] font-extrabold leading-none tracking-[-0.03em] tabular-nums text-ink-700"
        style={color ? { color } : undefined}
      >
        {value}
      </div>
      <div className="mt-1.5 font-market text-[9.5px] font-bold uppercase tracking-[0.16em] text-copy-label">
        {label}
      </div>
    </div>
  );
}
